'use client';

import { createClient } from '@/lib/supabase/client';

export type AuditActionType = 'auth' | 'data' | 'config' | 'threat' | 'admin';
export type AuditRiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';
export type AuditMfaStatus = 'verified' | 'bypass' | 'failed' | 'n/a';
export type AuditOutcome = 'success' | 'failed' | 'blocked';

export interface AuditLogEntry {
  action: string;
  action_type: AuditActionType;
  resource: string;
  risk_level?: AuditRiskLevel;
  mfa_status?: AuditMfaStatus;
  outcome?: AuditOutcome;
  actor?: string;
  actor_role?: string;
  ip_address?: string;
  location?: string;
  session_id?: string;
}

function generateLogId(): string {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `LOG-${ts}-${rand}`;
}

export async function insertAuditLog(entry: AuditLogEntry): Promise<void> {
  try {
    const supabase = createClient();

    // Get current user for actor info
    const { data: { user } } = await supabase.auth.getUser();

    const actor = entry.actor || user?.email || user?.id || 'system';
    const actorRole = entry.actor_role || user?.user_metadata?.role || 'analyst';

    const { error } = await supabase.from('audit_logs').insert({
      log_id: generateLogId(),
      event_timestamp: new Date().toISOString(),
      actor,
      actor_role: actorRole,
      action: entry.action,
      action_type: entry.action_type,
      resource: entry.resource,
      ip_address: entry.ip_address || null,
      location: entry.location || 'BD',
      risk_level: entry.risk_level || 'info',
      mfa_status: entry.mfa_status || 'n/a',
      session_id: entry.session_id || null,
      outcome: entry.outcome || 'success',
    });

    if (error) {
      // Non-critical — log silently
      console.warn('[AuditLogger] Insert failed:', error.message);
    }
  } catch {
    // Never throw — audit logging must not break app flow
  }
}

// ─── Convenience helpers ──────────────────────────────────────────────────────

export const auditLog = {
  // Auth events
  login: (actor: string, outcome: AuditOutcome = 'success') =>
    insertAuditLog({
      action: outcome === 'success' ? 'User login successful' : 'User login failed',
      action_type: 'auth',
      resource: '/login',
      risk_level: outcome === 'failed' ? 'medium' : 'info',
      mfa_status: 'n/a',
      outcome,
      actor,
    }),

  logout: (actor: string) =>
    insertAuditLog({
      action: 'User logout',
      action_type: 'auth',
      resource: '/logout',
      risk_level: 'info',
      mfa_status: 'n/a',
      outcome: 'success',
      actor,
    }),

  mfaVerified: (actor: string) =>
    insertAuditLog({
      action: 'MFA verification successful',
      action_type: 'auth',
      resource: '/mfa/verify',
      risk_level: 'info',
      mfa_status: 'verified',
      outcome: 'success',
      actor,
    }),

  mfaEnrolled: (actor: string) =>
    insertAuditLog({
      action: 'MFA enrollment completed',
      action_type: 'auth',
      resource: '/mfa/enroll',
      risk_level: 'low',
      mfa_status: 'verified',
      outcome: 'success',
      actor,
    }),

  mfaFailed: (actor: string) =>
    insertAuditLog({
      action: 'MFA verification failed',
      action_type: 'auth',
      resource: '/mfa/verify',
      risk_level: 'high',
      mfa_status: 'failed',
      outcome: 'failed',
      actor,
    }),

  // Settings events
  profileUpdated: () =>
    insertAuditLog({
      action: 'User profile settings updated',
      action_type: 'config',
      resource: '/settings/profile',
      risk_level: 'low',
      outcome: 'success',
    }),

  securitySettingsUpdated: () =>
    insertAuditLog({
      action: 'Security & MFA settings modified',
      action_type: 'config',
      resource: '/settings/security',
      risk_level: 'medium',
      outcome: 'success',
    }),

  preferencesUpdated: () =>
    insertAuditLog({
      action: 'User preferences updated',
      action_type: 'config',
      resource: '/settings/preferences',
      risk_level: 'info',
      outcome: 'success',
    }),

  apiKeyUpdated: (keyLabel: string) =>
    insertAuditLog({
      action: `API key updated: ${keyLabel}`,
      action_type: 'admin',
      resource: '/settings/api-keys',
      risk_level: 'high',
      outcome: 'success',
    }),

  apiKeyTested: (keyLabel: string, outcome: AuditOutcome) =>
    insertAuditLog({
      action: `API connection test: ${keyLabel}`,
      action_type: 'admin',
      resource: '/settings/api-keys',
      risk_level: outcome === 'failed' ? 'medium' : 'info',
      outcome,
    }),

  // Export events
  exportCSV: (recordCount: number, filters: string) =>
    insertAuditLog({
      action: `Audit log CSV export — ${recordCount} records`,
      action_type: 'data',
      resource: '/security-audit-logs/export/csv',
      risk_level: 'low',
      outcome: 'success',
    }),

  exportJSON: (recordCount: number) =>
    insertAuditLog({
      action: `Audit log JSON export — ${recordCount} records`,
      action_type: 'data',
      resource: '/security-audit-logs/export/json',
      risk_level: 'low',
      outcome: 'success',
    }),

  // Threat events
  threatDetected: (threatType: string, severity: AuditRiskLevel) =>
    insertAuditLog({
      action: `Threat detected: ${threatType}`,
      action_type: 'threat',
      resource: '/overview-dashboard/threat-feed',
      risk_level: severity,
      outcome: 'blocked',
    }),

  // AI assistant events
  aiQuerySubmitted: () =>
    insertAuditLog({
      action: 'AI Threat Assistant query submitted',
      action_type: 'data',
      resource: '/ai-threat-assistant',
      risk_level: 'info',
      outcome: 'success',
    }),
};
